import React, {useCallback, useEffect, useState} from 'react'

import {EuiFieldSearch, EuiHighlight, EuiInputPopover, EuiSelectable, EuiText, htmlIdGenerator} from '@elastic/eui'
import {EuiSelectableOption} from '@elastic/eui/src/components/selectable/selectable_option'
import {AudienceSegment} from "../../api/entities/AudienceSegment";
import {useGetInternetSegmentsQuery, useGetTvSegmentsQuery} from "../../api/rtkQueryApi/ToolsApi";
import {useDebounceEffect} from "../../utils/useDebounceEffect";

export interface SegmentTargeterProps {
    isInvalid: boolean

    onSegmentClicked(segments: AudienceSegment): void

    mode: AudienceType
}

export enum AudienceType {
    Tv = 'Tv',
    Internet = 'Internet'
}

export const SegmentFinder = ({onSegmentClicked, isInvalid, mode}: SegmentTargeterProps) => {
    const minimumLengthForSearch = 2
    const [debouncedSearchTurn, setDebouncedSearchTurn] = useState<string>('')
    const [searchTerm, setSearchTerm] = useState<string>('')
    const tvSegmentsQuery = useGetTvSegmentsQuery({query: debouncedSearchTurn}, {skip: mode != AudienceType.Tv || debouncedSearchTurn.length < minimumLengthForSearch})
    const internetSegmentsQuery = useGetInternetSegmentsQuery({query: debouncedSearchTurn}, {skip: mode != AudienceType.Internet || debouncedSearchTurn.length < minimumLengthForSearch})
    const [searchSegmentPopoverOpen, setSearchSegmentPopoverOpen] = useState<boolean>(false)
    const [segments, setSegments] = useState<AudienceSegment[]>([])

    useDebounceEffect(300, () => {
        setDebouncedSearchTurn(searchTerm)
    }, [searchTerm])

    useEffect(() => {
        if (mode != AudienceType.Tv) {
            return
        }

        if (tvSegmentsQuery.data) {
            setSegments([...tvSegmentsQuery.data])
        }
    }, [tvSegmentsQuery, mode]);

    useEffect(() => {
        if (mode != AudienceType.Internet) {
            return
        }

        if (internetSegmentsQuery.data) {
            setSegments([...internetSegmentsQuery.data])
        }
    }, [internetSegmentsQuery, mode]);

    const onSearchSegment = () => {
        setSearchSegmentPopoverOpen(false)
    }

    const onSearchSegmentChange = (hint: string) => {
        setSearchTerm(hint)
        if (!!hint) {
            setSearchSegmentPopoverOpen(true)
        }
    }

    const onSearchSegmentClick = useCallback(
        (segment: AudienceSegment) => {
            setSearchSegmentPopoverOpen(false)
            setDebouncedSearchTurn('')
            onSegmentClicked(segment)
        },
        [onSegmentClicked]
    )

    useEffect(() => {
        if (tvSegmentsQuery.data) {
            setSegments(tvSegmentsQuery.data)
        }
    }, [tvSegmentsQuery, internetSegmentsQuery]);

    const selectableSegments = segments.map(
        a =>
            ({
                label: a.name,
                key: a.name,
                checked: undefined,
                onClick: () => onSearchSegmentClick(a)
            } as EuiSelectableOption)
    )

    const renderAudienceOption = (option: EuiSelectableOption, _: string) => {
        const segment = segments.find(a => a.name === option.key)
        if (!segment) return <React.Fragment/>

        return (
            <React.Fragment>
                <EuiHighlight search={debouncedSearchTurn}>{option.label}</EuiHighlight>
                <EuiText style={{paddingLeft: '10px'}} size='xs'>
                    <EuiHighlight search={debouncedSearchTurn}>{segment.description ?? ''}</EuiHighlight>
                </EuiText>
            </React.Fragment>
        )
    }

    return (
        <EuiInputPopover
            fullWidth
            input={
                <EuiFieldSearch
                    id={htmlIdGenerator()()}
                    autoComplete='off'
                    fullWidth
                    compressed
                    value={searchTerm}
                    isLoading={tvSegmentsQuery.isLoading || internetSegmentsQuery.isLoading || tvSegmentsQuery.isFetching || internetSegmentsQuery.isFetching}
                    onChange={v => onSearchSegmentChange(v.target.value)}
                    onFocus={() => setSearchSegmentPopoverOpen(true)}
                    incremental={false}
                    onSearch={() => onSearchSegment()}
                    placeholder='Type to begin search...'
                    isInvalid={isInvalid}
                />
            }
            isOpen={searchSegmentPopoverOpen}
            closePopover={() => setSearchSegmentPopoverOpen(false)}>
            {segments && segments.length ? (
                <EuiSelectable singleSelection options={selectableSegments} renderOption={renderAudienceOption}
                               listProps={{rowHeight: 60, isVirtualized: true, showIcons: false}}>
                    {list => list}
                </EuiSelectable>
            ) : (
                <EuiText color='subdued' size='xs'>
                    <p>Start typing a search, results will appear here</p>
                </EuiText>
            )}
        </EuiInputPopover>
    )
}
